import { useState } from 'react';
import { useForm } from '@inertiajs/react';
import { format } from 'date-fns';
import { TripsLayout } from '../../components/layout/TripsLayout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { DatePicker } from '../../components/DatePicker';

interface NewTravelProps {
  travel: {
    city: string;
    reason: string;
    date_from: string;
    date_to: string;
    flight_cost: number;
    flight_url: string;
    hotel_cost: number;
    hotel_url: string;
  };
  routes: {
    travels_create: string;
    travels_index: string;
    [key: string]: string;
  };
  csrf_token: string;
  errors?: string[];
}

export default function New({ travel, routes, errors }: NewTravelProps) {
  const { data, setData, post, processing } = useForm({
    city: travel?.city || '',
    reason: travel?.reason || '',
    date_from: travel?.date_from || '',
    date_to: travel?.date_to || '',
    flight_cost: travel?.flight_cost || 0,
    flight_url: travel?.flight_url || '',
    hotel_cost: travel?.hotel_cost || 0,
    hotel_url: travel?.hotel_url || '',
  });

  // Convert string dates to Date objects for the date pickers
  const dateFromValue = data.date_from ? new Date(data.date_from) : undefined;
  const dateToValue = data.date_to ? new Date(data.date_to) : undefined;


  const handleCostChange = (field: 'flight_cost' | 'hotel_cost', value: string) => {
    // Remove leading zeros and non-numeric characters except decimal point
    let cleanValue = value.replace(/^0+(?=\d)/, '').replace(/[^\d.]/g, '');

    // Ensure only one decimal point
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      cleanValue = parts[0] + '.' + parts[1].slice(0, 2);
    }

    setData(field, cleanValue === '' ? 0 : parseFloat(cleanValue) || 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    post(routes.travels_create, {
      data: { travel: data }
    });
  };

  return (
    <TripsLayout>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-2xl">Request Travel</CardTitle>
          <CardDescription>Submit a new travel request for approval</CardDescription>
        </CardHeader>
      </Card>

      {errors && errors.length > 0 && (
        <Card className="mb-4 border-red-200">
          <CardContent className="pt-4 pb-4">
            <div className="text-red-600">
              <p className="font-semibold">Please fix the following errors:</p>
              <ul className="list-disc list-inside mt-1">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="max-w-2xl">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">
                Travel Details
              </CardTitle>
              <CardDescription>
                Provide information about your upcoming travel
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="city">Destination City</Label>
                <Input
                  id="city"
                  value={data.city}
                  onChange={(e) => setData('city', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Reason for Travel</Label>
                <Textarea
                  id="reason"
                  value={data.reason}
                  onChange={(e) => setData('reason', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="date_from">Start Date</Label>
                  <DatePicker
                    value={dateFromValue}
                    onChange={(date) => setData('date_from', date ? format(date, 'yyyy-MM-dd') : '')}
                    placeholder="Select start date"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date_to">End Date</Label>
                  <DatePicker
                    value={dateToValue}
                    onChange={(date) => setData('date_to', date ? format(date, 'yyyy-MM-dd') : '')}
                    placeholder="Select end date"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="flight_cost">Flight Cost</Label>
                  <Input
                    id="flight_cost"
                    type="text"
                    value={data.flight_cost === 0 ? '' : data.flight_cost.toString()}
                    onChange={(e) => handleCostChange('flight_cost', e.target.value)}
                    placeholder="0.00"
                    inputMode="decimal"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="flight_url">Flight Booking URL</Label>
                  <Input
                    id="flight_url"
                    type="url"
                    value={data.flight_url}
                    onChange={(e) => setData('flight_url', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="hotel_cost">Hotel Cost</Label>
                  <Input
                    id="hotel_cost"
                    type="text"
                    value={data.hotel_cost === 0 ? '' : data.hotel_cost.toString()}
                    onChange={(e) => handleCostChange('hotel_cost', e.target.value)}
                    placeholder="0.00"
                    inputMode="decimal"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hotel_url">Hotel Booking URL</Label>
                  <Input
                    id="hotel_url"
                    type="url"
                    value={data.hotel_url}
                    onChange={(e) => setData('hotel_url', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button type="submit" className="ml-auto" disabled={processing}>
                {processing ? 'Submitting...' : 'Submit Travel Request'}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </TripsLayout>
  );
}
